'use client'

import { forwardRef, useState } from 'react'
import { Check, Minus } from 'lucide-react'

/**
 * BIRHAUS Checkbox Component
 * 
 * Enhanced checkbox with clear state indicators implementing BIRHAUS principles:
 * - Clear visual feedback for all states
 * - Spanish-first labeling
 * - Accessibility-first design (WCAG AA+)
 * - Consistent with design token system
 */

// BIRHAUS: Utility function (self-contained)
function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ')
}

export interface BirhausCheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  // BIRHAUS Principle #7: Bilingual by design
  label?: string
  labelEs?: string
  labelEn?: string
  
  description?: string
  descriptionEs?: string
  descriptionEn?: string
  
  // Enhanced states
  indeterminate?: boolean
  
  // Styling variants
  variant?: 'default' | 'success' | 'warning' | 'error'
  size?: 'sm' | 'md' | 'lg'
  
  // Layout
  labelPosition?: 'right' | 'left'
  
  // State feedback
  showStateIcon?: boolean
  
  // Error handling
  error?: boolean
  errorMessage?: string
  errorMessageEs?: string
  errorMessageEn?: string
  
  // Success feedback
  success?: boolean
  successMessage?: string
  successMessageEs?: string
  successMessageEn?: string
  
  // Styling
  className?: string
  labelClassName?: string
}

export const BirhausCheckbox = forwardRef<HTMLInputElement, BirhausCheckboxProps>(({
  // Content (Spanish-first)
  label,
  labelEs,
  labelEn,
  description,
  descriptionEs,
  descriptionEn,
  
  // Enhanced states
  indeterminate = false,
  
  // Styling
  variant = 'default',
  size = 'md',
  labelPosition = 'right',
  showStateIcon = true,
  
  // State feedback
  error = false,
  errorMessage,
  errorMessageEs,
  errorMessageEn,
  success = false,
  successMessage,
  successMessageEs,
  successMessageEn,
  
  // Standard props
  checked,
  disabled,
  onChange,
  className,
  labelClassName,
  ...props
}, ref) => {
  
  // BIRHAUS: Internal state for uncontrolled usage
  const [internalChecked, setInternalChecked] = useState(checked || false)
  
  const isChecked = checked !== undefined ? checked : internalChecked
  const isControlled = checked !== undefined
  
  // BIRHAUS Principle #7: Spanish-first text resolution
  const resolveText = (spanish?: string, english?: string, fallback?: string) => {
    return spanish || english || fallback || ''
  }
  
  const displayLabel = resolveText(labelEs, labelEn, label)
  const displayDescription = resolveText(descriptionEs, descriptionEn, description)
  const displayErrorMessage = resolveText(errorMessageEs, errorMessageEn, errorMessage)
  const displaySuccessMessage = resolveText(successMessageEs, successMessageEn, successMessage)
  
  // BIRHAUS: Handle change
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!disabled) {
      const newChecked = event.target.checked
      
      if (!isControlled) {
        setInternalChecked(newChecked)
      }
      
      onChange?.(event)
    }
  }
  
  // BIRHAUS Principle #9: Consistency via tokens
  const sizeClasses = {
    sm: {
      checkbox: 'h-4 w-4',
      icon: 'h-3 w-3',
      label: 'text-sm',
      description: 'text-xs'
    },
    md: {
      checkbox: 'h-5 w-5',
      icon: 'h-4 w-4',
      label: 'text-base',
      description: 'text-sm'
    },
    lg: {
      checkbox: 'h-6 w-6',
      icon: 'h-5 w-5',
      label: 'text-lg',
      description: 'text-base'
    }
  }
  
  const variantClasses = {
    default: {
      border: 'border-gray-300',
      checked: 'bg-blue-600 border-blue-600',
      focus: 'focus:ring-blue-500'
    },
    success: {
      border: 'border-green-300',
      checked: 'bg-green-600 border-green-600',
      focus: 'focus:ring-green-500'
    },
    warning: {
      border: 'border-yellow-300',
      checked: 'bg-yellow-600 border-yellow-600',
      focus: 'focus:ring-yellow-500'
    },
    error: {
      border: 'border-red-300',
      checked: 'bg-red-600 border-red-600',
      focus: 'focus:ring-red-500'
    }
  }
  
  const currentSize = sizeClasses[size]
  const currentVariant = variantClasses[error ? 'error' : success ? 'success' : variant]
  
  const checkboxClasses = cn(
    // Base styles
    'relative rounded border-2 transition-colors duration-200',
    'focus:outline-none focus:ring-2 focus:ring-offset-2',
    
    // Size
    currentSize.checkbox,
    
    // State styles
    currentVariant.focus,
    
    // Checked/unchecked styles
    isChecked || indeterminate
      ? currentVariant.checked
      : cn('bg-white', currentVariant.border),
    
    // Disabled styles
    disabled && 'opacity-50 cursor-not-allowed',
    
    // Interactive styles
    !disabled && 'cursor-pointer hover:border-gray-400'
  )
  
  const labelClasses = cn(
    'font-medium text-gray-900',
    currentSize.label,
    disabled && 'text-gray-400',
    labelClassName
  )
  
  const containerClasses = cn(
    'flex items-start gap-3',
    labelPosition === 'left' && 'flex-row-reverse',
    className
  )
  
  return (
    <div className="space-y-2">
      <label className={containerClasses}>
        {/* Hidden native checkbox for accessibility */}
        <input
          ref={ref}
          type="checkbox"
          checked={isChecked}
          onChange={handleChange}
          disabled={disabled}
          className="sr-only"
          {...props}
        />
        
        {/* Custom checkbox */}
        <div className={checkboxClasses}>
          {/* Check/indeterminate icon */}
          {(isChecked || indeterminate) && showStateIcon && (
            <div className="absolute inset-0 flex items-center justify-center">
              {indeterminate ? (
                <Minus className={cn(currentSize.icon, 'text-white')} />
              ) : (
                <Check className={cn(currentSize.icon, 'text-white')} />
              )}
            </div>
          )}
        </div>
        
        {/* Label and description */}
        {(displayLabel || displayDescription) && (
          <div className="flex-1 min-w-0">
            {displayLabel && (
              <div className={labelClasses}>
                {displayLabel}
              </div>
            )}
            
            {displayDescription && (
              <div className={cn(
                'text-gray-600 mt-1',
                currentSize.description,
                disabled && 'text-gray-400'
              )}>
                {displayDescription}
              </div>
            )}
          </div>
        )}
      </label>
      
      {/* Error message */}
      {error && displayErrorMessage && (
        <div className={cn(
          'text-red-600 flex items-center gap-1',
          currentSize.description
        )}>
          <span>{displayErrorMessage}</span>
        </div>
      )}
      
      {/* Success message */}
      {success && displaySuccessMessage && (
        <div className={cn(
          'text-green-600 flex items-center gap-1',
          currentSize.description
        )}>
          <span>{displaySuccessMessage}</span>
        </div>
      )}
    </div>
  )
})

BirhausCheckbox.displayName = 'BirhausCheckbox'

// BIRHAUS: Switch component variant
export interface BirhausSwitchProps extends Omit<BirhausCheckboxProps, 'indeterminate' | 'showStateIcon'> {
  // Switch-specific props
  size?: 'sm' | 'md' | 'lg'
  
  // Visual indicators
  showLabels?: boolean
  onLabel?: string
  onLabelEs?: string
  onLabelEn?: string
  offLabel?: string
  offLabelEs?: string
  offLabelEn?: string
}

export const BirhausSwitch = forwardRef<HTMLInputElement, BirhausSwitchProps>(({
  // Content (Spanish-first)
  label,
  labelEs,
  labelEn,
  description,
  descriptionEs,
  descriptionEn,
  
  // Switch labels
  showLabels = false,
  onLabel = 'Sí',
  onLabelEs = 'Sí',
  onLabelEn = 'Yes',
  offLabel = 'No',
  offLabelEs = 'No',
  offLabelEn = 'No',
  
  // Styling
  variant = 'default',
  size = 'md',
  labelPosition = 'right',
  
  // State feedback
  error = false,
  errorMessage,
  errorMessageEs,
  errorMessageEn,
  success = false,
  successMessage,
  successMessageEs,
  successMessageEn,
  
  // Standard props
  checked,
  disabled,
  onChange,
  className,
  labelClassName,
  ...props
}, ref) => {
  
  // BIRHAUS: Internal state for uncontrolled usage
  const [internalChecked, setInternalChecked] = useState(checked || false)
  
  const isChecked = checked !== undefined ? checked : internalChecked
  const isControlled = checked !== undefined
  
  // BIRHAUS Principle #7: Spanish-first text resolution
  const resolveText = (spanish?: string, english?: string, fallback?: string) => {
    return spanish || english || fallback || ''
  }
  
  const displayLabel = resolveText(labelEs, labelEn, label)
  const displayDescription = resolveText(descriptionEs, descriptionEn, description)
  const displayErrorMessage = resolveText(errorMessageEs, errorMessageEn, errorMessage)
  const displaySuccessMessage = resolveText(successMessageEs, successMessageEn, successMessage)
  
  const displayOnLabel = resolveText(onLabelEs, onLabelEn, onLabel)
  const displayOffLabel = resolveText(offLabelEs, offLabelEn, offLabel)
  
  // BIRHAUS: Handle change
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!disabled) {
      const newChecked = event.target.checked
      
      if (!isControlled) {
        setInternalChecked(newChecked)
      }
      
      onChange?.(event)
    }
  }
  
  // BIRHAUS Principle #9: Consistency via tokens
  const sizeClasses = {
    sm: {
      track: 'h-5 w-9',
      thumb: 'h-4 w-4',
      translate: 'translate-x-4',
      label: 'text-sm',
      description: 'text-xs',
      switchLabel: 'text-xs'
    },
    md: {
      track: 'h-6 w-11',
      thumb: 'h-5 w-5',
      translate: 'translate-x-5',
      label: 'text-base',
      description: 'text-sm',
      switchLabel: 'text-xs'
    },
    lg: {
      track: 'h-7 w-13',
      thumb: 'h-6 w-6',
      translate: 'translate-x-6',
      label: 'text-lg',
      description: 'text-base',
      switchLabel: 'text-sm'
    }
  }
  
  const variantClasses = {
    default: {
      trackOff: 'bg-gray-200',
      trackOn: 'bg-blue-600',
      focus: 'focus:ring-blue-500'
    },
    success: {
      trackOff: 'bg-gray-200',
      trackOn: 'bg-green-600',
      focus: 'focus:ring-green-500'
    },
    warning: {
      trackOff: 'bg-gray-200',
      trackOn: 'bg-yellow-600',
      focus: 'focus:ring-yellow-500'
    },
    error: {
      trackOff: 'bg-gray-200',
      trackOn: 'bg-red-600',
      focus: 'focus:ring-red-500'
    }
  }
  
  const currentSize = sizeClasses[size]
  const currentVariant = variantClasses[error ? 'error' : success ? 'success' : variant]
  
  const containerClasses = cn(
    'flex items-start gap-3',
    labelPosition === 'left' && 'flex-row-reverse',
    className
  )
  
  const trackClasses = cn(
    'relative inline-flex items-center rounded-full border-2 border-transparent transition-colors duration-200',
    'focus:outline-none focus:ring-2 focus:ring-offset-2',
    currentSize.track,
    currentVariant.focus,
    isChecked ? currentVariant.trackOn : currentVariant.trackOff,
    disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
  )
  
  const thumbClasses = cn(
    'inline-block bg-white rounded-full shadow-lg transform transition-transform duration-200',
    currentSize.thumb,
    isChecked ? currentSize.translate : 'translate-x-0'
  )
  
  return (
    <div className="space-y-2">
      <label className={containerClasses}>
        {/* Hidden native checkbox for accessibility */}
        <input
          ref={ref}
          type="checkbox"
          checked={isChecked}
          onChange={handleChange}
          disabled={disabled}
          className="sr-only"
          role="switch"
          aria-checked={isChecked}
          {...props}
        />
        
        {/* Custom switch */}
        <div className="flex items-center gap-2">
          {showLabels && (
            <span className={cn(
              currentSize.switchLabel,
              'text-gray-600',
              !isChecked && 'font-medium text-gray-900'
            )}>
              {displayOffLabel}
            </span>
          )}
          
          <div className={trackClasses}>
            <div className={thumbClasses} />
          </div>
          
          {showLabels && (
            <span className={cn(
              currentSize.switchLabel,
              'text-gray-600',
              isChecked && 'font-medium text-gray-900'
            )}>
              {displayOnLabel}
            </span>
          )}
        </div>
        
        {/* Label and description */}
        {(displayLabel || displayDescription) && (
          <div className="flex-1 min-w-0">
            {displayLabel && (
              <div className={cn(
                'font-medium text-gray-900',
                currentSize.label,
                disabled && 'text-gray-400',
                labelClassName
              )}>
                {displayLabel}
              </div>
            )}
            
            {displayDescription && (
              <div className={cn(
                'text-gray-600 mt-1',
                currentSize.description,
                disabled && 'text-gray-400'
              )}>
                {displayDescription}
              </div>
            )}
          </div>
        )}
      </label>
      
      {/* Error message */}
      {error && displayErrorMessage && (
        <div className={cn(
          'text-red-600 flex items-center gap-1',
          currentSize.description
        )}>
          <span>{displayErrorMessage}</span>
        </div>
      )}
      
      {/* Success message */}
      {success && displaySuccessMessage && (
        <div className={cn(
          'text-green-600 flex items-center gap-1',
          currentSize.description
        )}>
          <span>{displaySuccessMessage}</span>
        </div>
      )}
    </div>
  )
})

BirhausSwitch.displayName = 'BirhausSwitch'

// BIRHAUS: Hook for managing checkbox/switch state
export function useBirhausCheckbox(initialChecked = false) {
  const [checked, setChecked] = useState(initialChecked)
  const [error, setError] = useState(false)
  const [success, setSuccess] = useState(false)
  
  const toggle = () => setChecked(prev => !prev)
  const reset = () => {
    setChecked(initialChecked)
    setError(false)
    setSuccess(false)
  }
  
  return {
    checked,
    setChecked,
    error,
    setError,
    success,
    setSuccess,
    toggle,
    reset
  }
}

// Components and utilities are exported via individual export declarations above